import { MetricOverrideType, VariationInputMap } from '@features/question-configuration';
import {
  AggregationConfigOverride,
  InitiativeUniversalTracker,
  MergedValueListOption,
  TableOverride,
  ValueListConfig,
} from '../types/initiativeUniversalTracker';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { UniversalTrackerBlueprintMin } from '@g17eco/types/universalTracker';
import { Tag } from '@g17eco/types/metricGroup';
import { AggregationValueType } from '../types/universalTracker';

export interface InputOverrideParams {
  initiativeId: string;
  decimal?: Record<string, number | null>;
  unitConfig?: Record<string, Record<string, string | boolean>>;
  variation?: VariationInputMap;
  mandatory?: Record<string, boolean>;
  utrIds: string[];
}

export interface MetricOverrideParams {
  initiativeId: string;
  utrvConfig?: Partial<
    Pick<MetricOverrideType, 'verificationRequired' | 'evidenceRequired' | 'noteRequired' | 'isPrivate'>
  >;
  aiProcessing?: MetricOverrideType['aiProcessing'];
  utrIds: string[];
}

interface AggregationOverrideParams {
  initiativeId: string;
  aggregationConfig: AggregationConfigOverride | undefined;
  table: TableOverride | undefined;
  utrId: string;
  aggregationValueType?: AggregationValueType | 'default';
}

export interface InitiativeQuestionResponse {
  utr: UniversalTrackerBlueprintMin;
  tags: Tag['groupName'][];
  rootInitiativeUtr?: InitiativeUniversalTracker;
  valueList?: ValueListConfig<MergedValueListOption>;
}

export interface UtrValueListCustomOptionsResponse {
  name: string;
  usage: { utrvIds: string[]; initiativeIds: string[] };
}

enum ApiTag {
  InitiativeUniversalTrackers = 'initiative-universal-trackers',
  InitiativeUniversalTrackersValueListCustomOptions = 'initiative-universal-trackers-value-list-custom-options',
  SubsidiaryUniversalTrackers = 'subsidiary-universal-trackers',
}

const getUrl = ({ initiativeId }: { initiativeId: string }) =>
  `/initiative-universal-trackers/initiative/${initiativeId}`;

export const initiativeUniversalTrackersApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: Object.values(ApiTag),
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getInitiativeUniversalTrackers: builder.query<InitiativeUniversalTracker<MergedValueListOption>[], string>({
        transformResponse,
        query: (initiativeId) => ({
          url: getUrl({ initiativeId }),
          method: 'get',
        }),
        providesTags: (_result, _error, initiativeId) => [
          { type: ApiTag.InitiativeUniversalTrackers, id: initiativeId },
        ],
      }),
      updateInitiativeUniversalTracker: builder.mutation<InitiativeUniversalTracker, InputOverrideParams>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: getUrl({ initiativeId }),
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: ApiTag.InitiativeUniversalTrackers, id: arg.initiativeId }],
      }),
      updateMetricOverrides: builder.mutation<InitiativeUniversalTracker[], MetricOverrideParams>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/metric-overrides`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: ApiTag.InitiativeUniversalTrackers, id: arg.initiativeId }],
      }),
      overrideAggregation: builder.mutation<InitiativeUniversalTracker, AggregationOverrideParams>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/override/aggregation`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: ApiTag.InitiativeUniversalTrackers, id: arg.initiativeId }],
      }),
      getInitiativeQuestion: builder.query<InitiativeQuestionResponse, { initiativeId: string; utrId: string }>({
        transformResponse,
        query: ({ initiativeId, utrId }) => ({
          url: `initiatives/${initiativeId}/questions/${utrId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, arg) => [{ type: ApiTag.InitiativeUniversalTrackers, id: arg.initiativeId }],
      }),
      overrideValueList: builder.mutation<
        InitiativeUniversalTracker[],
        { initiativeId: string; utrId: string; valueListConfig: ValueListConfig }
      >({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/value-list`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [
          { type: ApiTag.InitiativeUniversalTrackers, id: arg.initiativeId },
          { type: ApiTag.InitiativeUniversalTrackersValueListCustomOptions, id: arg.utrId },
        ],
      }),
      getUtrValueListCustomOptions: builder.query<
        UtrValueListCustomOptionsResponse[],
        { initiativeId: string; utrId: string; columnCode?: string }
      >({
        transformResponse,
        query: ({ initiativeId, utrId, columnCode }) => ({
          url: `${getUrl({ initiativeId })}/universal-tracker/${utrId}/value-list/custom-options`,
          method: 'get',
          params: { columnCode },
        }),
        providesTags: (_result, _error, arg) => [
          { type: ApiTag.InitiativeUniversalTrackersValueListCustomOptions, id: arg.utrId },
        ],
      }),
      getSubsidiaryUniversalTrackers: builder.query<InitiativeUniversalTracker<MergedValueListOption>[], string>({
        transformResponse,
        query: (initiativeId) => ({
          url: `/initiative-universal-trackers/subsidiaries/${initiativeId}`,
          method: 'get',
        }),
        providesTags: (_result, _error, initiativeId) => [
          { type: ApiTag.SubsidiaryUniversalTrackers, id: initiativeId },
        ],
      }),
      saveSubsidiaryCustomOption: builder.mutation<
        { success: boolean },
        { initiativeId: string; utrvId: string; code: string; name?: string; columnCode?: string }
      >({
        query: ({ initiativeId, utrvId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/universal-tracker-value/${utrvId}/value-list/custom-options`,
          method: 'post',
          data,
        }),
        invalidatesTags: (_result, _error, arg) => [{ type: ApiTag.SubsidiaryUniversalTrackers, id: arg.initiativeId }],
      }),
    }),
  });

export const {
  endpoints,
  useGetInitiativeUniversalTrackersQuery,
  useUpdateInitiativeUniversalTrackerMutation,
  useUpdateMetricOverridesMutation,
  useOverrideAggregationMutation,
  useGetInitiativeQuestionQuery,
  useOverrideValueListMutation,
  useGetUtrValueListCustomOptionsQuery,
  useGetSubsidiaryUniversalTrackersQuery,
  useSaveSubsidiaryCustomOptionMutation,
} = initiativeUniversalTrackersApi;
